import type { UIAuditCategory } from "@/packages/types";
import { ClipboardList } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { WidgetCard, WidgetCardSkeleton } from "./WidgetCard";

interface RecentChecklistCardProps {
  categories: UIAuditCategory[];
  onNavigateToItem: (itemId: string) => void;
  onGoToChecklist: () => void;
  index?: number;
}

interface FlatItem {
  id: string;
  name: string;
  status: string;
  categoryName: string;
  lastModified: number;
}

function StatusDot({ status }: { status: string }) {
  if (status === "APPROVED") {
    return <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />;
  }
  if (status === "UPLOADED") {
    return (
      <span className="w-2.5 h-2.5 rounded-full border-2 border-accent flex items-center justify-center">
        <span className="w-1 h-1 rounded-full bg-amber-400" />
      </span>
    );
  }
  return <span className="w-2.5 h-2.5 rounded-full border-2 border-border" />;
}

function flattenAndSort(categories: UIAuditCategory[]): FlatItem[] {
  const items: FlatItem[] = [];
  for (const cat of categories) {
    for (const item of cat.items) {
      items.push({
        id: item.id,
        name: item.name,
        status: item.status,
        categoryName: cat.name,
        lastModified: item.lastModified ?? 0,
      });
    }
  }

  const hasModified = items.some((i) => i.lastModified > 0);
  if (hasModified) {
    items.sort((a, b) => b.lastModified - a.lastModified);
  }

  return items.slice(0, 5);
}

export function RecentChecklistCard({
  categories,
  onNavigateToItem,
  onGoToChecklist,
  index,
}: RecentChecklistCardProps) {
  const items = flattenAndSort(categories);

  return (
    <WidgetCard
      size="L"
      title="Recent Checklist"
      icon={<ClipboardList size={16} />}
      action={
        <button
          type="button"
          className="text-xs font-medium text-accent hover:text-accent/80"
          onClick={onGoToChecklist}
        >
          Go to Checklist &rarr;
        </button>
      }
      index={index}
    >
      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full gap-1">
          <ClipboardList size={32} className="text-muted" />
          <span className="text-sm text-muted">No checklist items yet</span>
        </div>
      ) : (
        <div className="flex flex-col">
          {/* Header row */}
          <div className="flex items-center py-1.5 text-xs text-muted uppercase tracking-wide border-b border-border/30 pb-1">
            <span className="w-[28px] shrink-0" />
            <span className="flex-1">Item</span>
            <span className="w-[160px] hidden md:block">Category</span>
            <span className="w-[100px] text-right">Last Modified</span>
          </div>

          {/* Data rows */}
          {items.map((item, i) => (
            <button
              type="button"
              key={item.id}
              className={`flex items-center py-2.5 border-b border-border/50 last:border-0 cursor-pointer rounded-md text-left transition-all duration-150 hover:pl-2 hover:border-l-2 hover:border-l-accent/40 ${
                i % 2 === 1 ? "bg-surface/20" : ""
              }`}
              onClick={() => onNavigateToItem(item.id)}
            >
              <span className="w-[28px] shrink-0 flex items-center justify-center">
                <StatusDot status={item.status} />
              </span>
              <span className="flex-1 text-sm text-secondary truncate">
                {item.name}
              </span>
              <span className="w-[160px] text-xs text-muted truncate hidden md:block">
                {item.categoryName}
              </span>
              <span className="w-[100px] text-xs text-muted text-right">
                {item.lastModified > 0
                  ? formatDistanceToNow(new Date(item.lastModified), {
                      addSuffix: true,
                    })
                  : "--"}
              </span>
            </button>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}

export function RecentChecklistCardSkeleton({ index }: { index?: number }) {
  return <WidgetCardSkeleton size="L" index={index} />;
}
